// Bausteine rund ums Hosting (h-ui): Support-Band, Hinweis bei erreichter
// Tarifgrenze, Nutzungsbalken und die Zahlenformate dazu.
//
// Das Band und der Hinweis hängen im Layout und erscheinen nur, wenn es etwas
// zu sagen gibt. Die Balken nutzen „Mein Tarif“ und die Betreiberseiten.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GRENZEN_NAMEN, supportBeenden, supportSitzung } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { can } from "@webradiosuite/shared";
import type { Grenzen, Nutzung, RadioStatus } from "../types";
import { Badge } from "./ui";
import { Sym } from "./social-ui";

// Speicher-Grenzen sind in GB/MB angegeben. Die Suite rechnet binär
// (1 GB = 1024³ Byte) – wie die Anzeige der Mediathek.
export const GB = 1024 ** 3;
export const MB = 1024 ** 2;

const zahl = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });
const ganz = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

export function bytesText(bytes: number | null | undefined): string {
  if (bytes == null) return "–";
  if (bytes >= GB) return `${zahl.format(bytes / GB)} GB`;
  if (bytes >= MB) return `${zahl.format(bytes / MB)} MB`;
  if (bytes >= 1024) return `${ganz.format(bytes / 1024)} KB`;
  return `${ganz.format(bytes)} Byte`;
}

export function zahlText(n: number | null | undefined, stellen = 0): string {
  if (n == null) return "–";
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: stellen }).format(n);
}

export function datumText(iso: string | null | undefined, mitZeit = false): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", mitZeit
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" });
}

export const STATUS_ANZEIGE: Record<RadioStatus, { label: string; tone: "success" | "danger" | "warning" }> = {
  aktiv: { label: "aktiv", tone: "success" },
  gesperrt: { label: "gesperrt", tone: "danger" },
  loeschen_vorgemerkt: { label: "Löschung vorgemerkt", tone: "warning" },
};

export function StatusBadgeRadio({ status }: { status: RadioStatus }): JSX.Element {
  const s = STATUS_ANZEIGE[status] ?? { label: status, tone: "warning" as const };
  return <Badge tone={s.tone} dot>{s.label}</Badge>;
}

/** Reihenfolge und Art der Grenzen – Zahl (mit Einheit) oder Schalter. */
export const GRENZEN_FELDER: Array<
  | { key: keyof Grenzen; art: "zahl"; einheit: string; schritt?: number; hilfe: string }
  | { key: keyof Grenzen; art: "schalter"; hilfe: string }
> = [
  { key: "kanaele", art: "zahl", einheit: "Kanäle", hilfe: "Wie viele Kanäle das Radio anlegen darf." },
  { key: "mediathek_gb", art: "zahl", einheit: "GB", schritt: 0.5, hilfe: "Speicher für Musik, Jingles und Beiträge." },
  { key: "bilder_mb", art: "zahl", einheit: "MB", hilfe: "Speicher für Bilder der Hörerseite." },
  { key: "hoerer_gleichzeitig", art: "zahl", einheit: "Hörer", hilfe: "Höchstens so viele gleichzeitig, je Kanal." },
  { key: "bitrate_max_kbps", art: "zahl", einheit: "kbit/s", hilfe: "Höchste Bitrate eines Ausgangs." },
  { key: "studio_geraete", art: "zahl", einheit: "Geräte", hilfe: "Gekoppelte Studio-Rechner." },
  { key: "kanalseiten", art: "schalter", hilfe: "Eigene Hörerseite je Kanal." },
  { key: "eigene_domain", art: "schalter", hilfe: "Hörerseite unter eigener Adresse." },
  { key: "mitschnitt", art: "schalter", hilfe: "Sendemitschnitt aufzeichnen." },
];

/** Was von der Nutzung zu welcher Grenze gehört – in der Einheit der Grenze. */
export function nutzungFuer(key: keyof Grenzen, n: Nutzung | undefined): { wert: number | null; text: string } {
  if (!n) return { wert: null, text: "–" };
  switch (key) {
    case "kanaele": return { wert: n.kanaele ?? null, text: zahlText(n.kanaele) };
    case "mediathek_gb": return { wert: n.mediathek_bytes != null ? n.mediathek_bytes / GB : null, text: bytesText(n.mediathek_bytes) };
    case "bilder_mb": return { wert: n.bilder_bytes != null ? n.bilder_bytes / MB : null, text: bytesText(n.bilder_bytes) };
    case "studio_geraete": return { wert: n.studio_geraete ?? null, text: zahlText(n.studio_geraete) };
    case "hoerer_gleichzeitig": {
      // Die Grenze gilt je Kanal: der vollste Kanal zählt.
      const v = n.hoerer_jetzt_max_kanal ?? n.hoerer_spitze ?? n.hoerer_jetzt ?? null;
      return { wert: v, text: zahlText(v) };
    }
    case "bitrate_max_kbps": return { wert: n.bitrate_max_kbps ?? null, text: n.bitrate_max_kbps != null ? `${n.bitrate_max_kbps} kbit/s` : "–" };
    default: return { wert: null, text: "–" };
  }
}

export function grenzeText(key: keyof Grenzen, g: Grenzen): string {
  const v = g[key];
  const feld = GRENZEN_FELDER.find((f) => f.key === key);
  if (v == null) return "unbegrenzt";
  if (typeof v === "boolean") return v ? "enthalten" : "nicht enthalten";
  const einzahl: Partial<Record<keyof Grenzen, string>> = { kanaele: "Kanal", studio_geraete: "Gerät", hoerer_gleichzeitig: "Hörer" };
  const einheit = v === 1 && einzahl[key] ? einzahl[key] : feld && feld.art === "zahl" ? feld.einheit : "";
  return `${zahlText(v, 1)} ${einheit}`.trim();
}

/**
 * Ein Balken je Grenze: belegt / erlaubt. Unbegrenztes steht ohne Balken da,
 * Schalter als Etikett. Über der Grenze wird rot – gelöscht wird nichts.
 */
export function Nutzungsbalken({ grenzen, nutzung, kompakt = false }: { grenzen: Grenzen; nutzung?: Nutzung; kompakt?: boolean }): JSX.Element {
  return (
    <ul className={`grid gap-x-6 ${kompakt ? "gap-y-3" : "gap-y-4"} sm:grid-cols-2`}>
      {GRENZEN_FELDER.map((f) => {
        const erlaubt = grenzen[f.key];
        if (f.art === "schalter") {
          return (
            <li key={f.key} className="flex min-w-0 items-center justify-between gap-3">
              <span className="min-w-0 truncate text-sm text-secondary">{GRENZEN_NAMEN[f.key]}</span>
              {erlaubt === false
                ? ((nutzung?.[f.key as keyof Nutzung] as number | undefined) ?? 0) > 0
                  ? <Badge tone="danger" className="flex-shrink-0">nicht enthalten, aber in Gebrauch</Badge>
                  : <Badge tone="neutral" className="flex-shrink-0">nicht enthalten</Badge>
                : <Badge tone="success" className="flex-shrink-0">enthalten</Badge>}
            </li>
          );
        }
        const n = nutzungFuer(f.key, nutzung);
        const grenze = typeof erlaubt === "number" ? erlaubt : null;
        const anteil = grenze != null && n.wert != null ? (grenze === 0 ? (n.wert > 0 ? 2 : 0) : n.wert / grenze) : null;
        const ueber = anteil != null && anteil > 1;
        const farbe = ueber ? "bg-red-500" : anteil != null && anteil >= 0.8 ? "bg-amber-500" : "bg-brand-primary";
        return (
          <li key={f.key} className="min-w-0">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-secondary" title={f.key === "hoerer_gleichzeitig" ? "Zählt: Hörer gerade im vollsten Kanal" : undefined}>{GRENZEN_NAMEN[f.key]}</span>
              <span className={`flex-shrink-0 tabular-nums ${ueber ? "font-semibold text-red-600 dark:text-red-400" : "text-primary"}`}>
                {n.text}
                <span className="text-muted"> / {grenzeText(f.key, grenzen)}</span>
              </span>
            </div>
            {grenze != null ? (
              <div
                className="mt-1.5 h-2 overflow-hidden rounded-full bg-input"
                role="meter"
                aria-label={GRENZEN_NAMEN[f.key]}
                aria-valuemin={0}
                aria-valuemax={grenze}
                aria-valuenow={n.wert ?? 0}
              >
                <div className={`h-full rounded-full ${farbe}`} style={{ width: `${Math.min(100, Math.round((anteil ?? 0) * 100))}%` }} />
              </div>
            ) : (
              <div className="mt-1.5 h-2 rounded-full bg-input opacity-60" aria-hidden="true" />
            )}
            {ueber && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Über der Grenze – Bestehendes bleibt, Neues geht erst nach dem Aufräumen oder mit größerem Tarif.</p>}
          </li>
        );
      })}
    </ul>
  );
}

function restText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Band ganz oben während eines Support-Zugriffs. Zählt herunter und bringt
 * den Betreiber bei 0 von selbst in die eigene Sitzung zurück.
 */
export function SupportBand(): JSX.Element | null {
  const [sitzung] = useState(() => supportSitzung());
  const [jetzt, setJetzt] = useState(() => Date.now());
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!sitzung) return;
    const t = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [sitzung]);

  const rest = sitzung ? Date.parse(sitzung.ende) - jetzt : 0;
  useEffect(() => {
    if (sitzung && rest <= 0 && !laeuft) {
      setLaeuft(true);
      void supportBeenden();
    }
  }, [sitzung, rest, laeuft]);

  if (!sitzung) return null;
  const knapp = rest < 5 * 60_000;
  return (
    <div
      className="relative z-50 flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 bg-amber-400 px-3 py-2 text-sm text-[#451a03] shadow-sm sm:px-6"
      role="status"
      aria-live="polite"
    >
      <Sym name="shield" className="h-4 w-4" />
      <span className="min-w-0 flex-1">
        <b className="font-semibold">Support-Zugriff</b> bei <b className="font-semibold">{sitzung.radio_name}</b>
        {sitzung.art === "notfall" && <span className="ml-1.5 rounded bg-[#451a03]/15 px-1.5 py-px text-xs font-medium">Notfall</span>}
        <span className="text-[#451a03]/80"> – endet in </span>
        <span className={`tabular-nums font-semibold ${knapp ? "underline decoration-2" : ""}`}>{restText(rest)}</span>
        <span className="hidden text-[#451a03]/80 md:inline"> · Alles, was du änderst, steht im Protokoll des Radios.</span>
      </span>
      <button
        type="button"
        disabled={laeuft}
        onClick={() => { setLaeuft(true); void supportBeenden(); }}
        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-950 px-3 text-xs font-semibold text-amber-50 hover:bg-amber-900 disabled:opacity-60"
      >
        <Sym name="x" className="h-3.5 w-3.5" />
        {laeuft ? "Wird beendet …" : <span>Beenden<span className="hidden sm:inline"> &amp; zurück</span></span>}
      </button>
    </div>
  );
}

/**
 * Hinweis, wenn irgendwo eine Tarifgrenze zugeschlagen hat (409 limit_reached).
 * api.ts schickt das Ereignis; die Seite selbst zeigt den Satz ohnehin – hier
 * steht zusätzlich der Weg zu „Mein Tarif“.
 */
export function GrenzeHinweis(): JSX.Element | null {
  const { user } = useAuth();
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    let t: number | undefined;
    const onGrenze = (e: Event): void => {
      const d = (e as CustomEvent<{ message?: string }>).detail;
      setMeldung(d?.message ?? "Eine Grenze des Tarifs ist erreicht.");
      window.clearTimeout(t);
      t = window.setTimeout(() => setMeldung(null), 15_000);
    };
    window.addEventListener("wrs:grenze", onGrenze);
    return () => { window.removeEventListener("wrs:grenze", onGrenze); window.clearTimeout(t); };
  }, []);

  if (!meldung) return null;
  const darfTarif = user?.role ? can(user.role, "tenant.manage") : false;
  return (
    // Unten fest angeheftet: Man sieht ihn auch, wenn die Seite weit gescrollt ist.
    <div className="fixed inset-x-4 bottom-4 z-40 animate-fade-in rounded-lg bg-card shadow-lg sm:left-auto sm:right-6 sm:w-[30rem]">
      <div className="callout callout-warning items-start" role="alert">
        <Sym name="alert" className="mt-0.5 h-4 w-4" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Grenze des Tarifs erreicht</div>
          <div className="mt-0.5 opacity-90">
            {meldung}{" "}
            {darfTarif
              ? <Link to="/tarif" className="font-medium underline" onClick={() => setMeldung(null)}>Mein Tarif ansehen</Link>
              : <span>Die Radioleitung kann den Tarif beim Betreiber erweitern lassen.</span>}
          </div>
        </div>
        <button type="button" onClick={() => setMeldung(null)} className="rounded p-0.5 opacity-70 hover:opacity-100" aria-label="Hinweis schließen">
          <Sym name="x" className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
